import { useState } from 'react';
import Box from '@mui/material/Box';
import { UsageType, UsageTypeValue } from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import { ChartType, ChartTypeValue } from 'src/common/components/ChartTypeToggle/ChartTypeToggle';
import { usageTooltipValueFormatter } from 'src/common/utils/mapUsageDTOtoAreaChartData';
import { AreaChart } from 'src/common/components/Graphs/common/Graphs/AreaChart';
import { useDetailsDailyUsageData } from './hooks/useDetailsDailyUsageData';
import { BarChart } from 'src/common/components/Graphs/common/Graphs/BarChart';
import { useDetailsMonthlyUsageData } from './hooks/useDetailMonthlyUsageData';
import { ObjectValues } from 'src/common/tsHelpers';
import { detailsQueries } from './queries/detailsQueries';
import { useHasUsageChartData } from 'src/dashboard/hooks/useHasUsageChartData';
import { UsageCharts } from '../../Graphs/common/Graphs/UsageCharts';
import { pxToRem } from 'src/common/utils/fonts';
import { getDefaultBarChartLabels } from 'src/common/components/Graphs/common/Graphs/utils/getDefaultBarChartLabels';
import { useChartDataLastUpdated } from 'src/dashboard/hooks/useChartDataLastUpdated';

const usageChartTooltipLabelOrder = {
  Limit: 0,
  Total: 1,
  Upload: 2,
  Donwload: 3,
  MO: 2,
  MT: 3,
};

export const DetailsType = {
  Sim: 'Sim',
  RSim: 'RSim',
} as const;
export type DetailsTypeValue = ObjectValues<typeof DetailsType>;

type UsageChartSectionProps = {
  detailsType: DetailsTypeValue;
  fetchBy: string;
};

export const UsageChartSection = ({ detailsType, fetchBy }: UsageChartSectionProps) => {
  const [usageType, setUsageType] = useState<UsageTypeValue>(UsageType.Data);
  const [chartType, setChartType] = useState<ChartTypeValue>(ChartType.Cumulative);

  const hasMonthlyData = useHasUsageChartData(
    detailsQueries.monthlyUsage({ id: fetchBy, usageType, detailsType }),
  );

  const hasDailyData = useHasUsageChartData(
    detailsQueries.dailyUsage({
      id: fetchBy,
      chartType,
      usageType,
      detailsType,
    }),
  );

  const lastUpdatedDate = useChartDataLastUpdated(
    detailsQueries.dailyUsage({
      id: fetchBy,
      chartType,
      usageType,
      detailsType,
    }),
  );

  const shouldDisplayLegend = hasMonthlyData || hasDailyData;

  const toggleType = () => {
    setChartType((p) =>
      p === ChartType.Cumulative ? ChartType.NonCumulative : ChartType.Cumulative,
    );
  };

  return (
    <Box
      sx={{
        marginBottom: 20,
        width: '100%',
        height: {
          xs: 420,
          md: pxToRem(220),
        },
      }}
    >
      <UsageCharts
        usageType={usageType}
        setUsageType={setUsageType}
        chartType={chartType}
        toggleChartType={toggleType}
        displayChartsTitle
        displayMonthlyChart={true}
        monthlyChart={
          <MothlyUsageBarChart id={fetchBy} usageType={usageType} detailsType={detailsType} />
        }
        displayDailyChart={true}
        dailyChart={
          <DailyUsageAreaChart
            id={fetchBy}
            usageType={usageType}
            chartType={chartType}
            detailsType={detailsType}
          />
        }
        shouldDisplayLegend={shouldDisplayLegend}
        lastUpdated={lastUpdatedDate}
      />
    </Box>
  );
};

const MothlyUsageBarChart = ({
  id,
  usageType,
  detailsType,
}: {
  id: string;
  detailsType: DetailsTypeValue;
  usageType: UsageTypeValue;
}) => {
  const monthlyUsageData = useDetailsMonthlyUsageData({ id, usageType, detailsType });

  const monthlyUsageXAxisLabels = monthlyUsageData.datasets.length
    ? monthlyUsageData.xAxisLabels
    : getDefaultBarChartLabels(true);

  return (
    <BarChart
      xAxisLabels={monthlyUsageXAxisLabels}
      datasets={monthlyUsageData.datasets}
      yAxisLabel={monthlyUsageData.yAxisLabel}
      tooltipLabelPriority={usageChartTooltipLabelOrder}
      tooltipValueFormatter={(value: number, unit?: string) =>
        usageTooltipValueFormatter(value, usageType, unit)
      }
    />
  );
};

const DailyUsageAreaChart = ({
  id,
  usageType,
  chartType,
  detailsType,
}: {
  id: string;
  usageType: UsageTypeValue;
  chartType: ChartTypeValue;
  detailsType: DetailsTypeValue;
}) => {
  const dailyUsageData = useDetailsDailyUsageData({
    id,
    usageType,
    chartType,
    detailsType,
  });

  return (
    <AreaChart
      datasets={dailyUsageData.datasets}
      limit={dailyUsageData.limit}
      yAxisLabel={dailyUsageData.yAxisLabel}
      xAxisLabels={dailyUsageData.xAxisLabels}
      realDataEndIndex={dailyUsageData.realDataEndIndex}
      tooltipLabelPriority={usageChartTooltipLabelOrder}
      tooltipValueFormatter={(value: number, unit?: string) =>
        usageTooltipValueFormatter(value, usageType, unit)
      }
    />
  );
};
